import React, {useCallback} from 'react'
import {ReactDatePickerProps} from 'react-datepicker'

import {Icon} from '../icon/Icon'
import {StyledDatePicker, StyledDatePickerWrapper} from './DatePicker.styled'

import {theme} from '~/styles/theme'

import './datePickerStyle'

type ExtendedDatePickerProps = ReactDatePickerProps & {
  enableTwoDigitYearInput?: boolean
  twoDigitYearPivot?: number // e.g. 2010 => yy <= 10 maps to 20yy, else 19yy
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
}

export const DatePicker: React.FC<ExtendedDatePickerProps> = (props) => {
  const {
    showTimeSelectOnly,
    dateFormat,
    onChange,
    onChangeRaw,
    enableTwoDigitYearInput,
    twoDigitYearPivot,
    inputProps,
    ...rest
  } = props

  const effectiveDateFormat = showTimeSelectOnly
    ? 'h:mm aa'
    : (dateFormat as string | string[] | undefined) ?? 'dd/MM/yyyy'

  // Intentionally empty dependency array: this callback does not capture props/state;
  // it only depends on its parameters (input, pivot), so memoizing once is safe.
  const parseWithPivot = useCallback(
    (input: string, pivot: number): Date | null => {
      // Accept dd/MM/yy or dd/MM/yyyy; allow '-', '.', and spaces as separators
      const normalized = input.trim().replace(/[-.]/g, '/').replace(/\s+/g, '')
      const parts = normalized.split('/')
      if (parts.length !== 3) return null
      const [ddStr, mmStr, yyStr] = parts
      const day = Number(ddStr)
      const month = Number(mmStr)
      if (!Number.isInteger(day) || !Number.isInteger(month)) return null
      if (yyStr.length !== 2 && yyStr.length !== 4) return null
      let year = Number(yyStr)
      if (yyStr.length === 2) {
        const pivotShort = pivot % 100
        // Pivot rule: with pivot yyyy, pivotShort = yyyy % 100.
        // Example pivot 2010: yy <= 10 => 2000 + yy; else 1900 + yy (e.g., 04->2004, 27->1927)
        year = year <= pivotShort ? 2000 + year : 1900 + year
      }
      if (month < 1 || month > 12 || day < 1 || day > 31) return null
      const date = new Date(year, month - 1, day)
      if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
      ) {
        return null
      }
      return date
    },
    [],
  )

  const formatDdMmYyyy = useCallback((d: Date): string => {
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${dd}/${mm}/${yyyy}`
  }, [])

  const handleChange = useCallback<
    NonNullable<ReactDatePickerProps['onChange']>
  >(
    (date, event) => {
      if (enableTwoDigitYearInput && twoDigitYearPivot && !showTimeSelectOnly) {
        const raw = (event?.target as HTMLInputElement | undefined)?.value ?? ''
        const normalized = raw.trim().replace(/[-.]/g, '/').replace(/\s+/g, '')
        const parts = normalized.split('/')
        const isTwoDigitYear = parts.length === 3 && parts[2].length === 2
        if (isTwoDigitYear) {
          const yearStr = parts[2]
          // If year is "19" or "20", discard and wait for 2 more digits
          if (yearStr === '19' || yearStr === '20') {
            return
          }
          const corrected = parseWithPivot(raw, twoDigitYearPivot)
          if (corrected) {
            onChange?.(corrected, event)
            return
          }
        }
      }
      onChange?.(date, event)
    },
    [
      enableTwoDigitYearInput,
      twoDigitYearPivot,
      showTimeSelectOnly,
      parseWithPivot,
      onChange,
    ],
  )

  const handleChangeRaw = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (enableTwoDigitYearInput && twoDigitYearPivot && !showTimeSelectOnly) {
        const raw = (event.target as HTMLInputElement | undefined)?.value ?? ''
        const normalized = raw.trim().replace(/[-.]/g, '/').replace(/\s+/g, '')
        const parts = normalized.split('/')
        const isTwoDigitYear = parts.length === 3 && parts[2].length === 2
        if (isTwoDigitYear) {
          const yearStr = parts[2]
          // If year is "19" or "20", discard and wait for 2 more digits
          if (yearStr === '19' || yearStr === '20') {
            onChangeRaw?.(event)
            return
          }
          const corrected = parseWithPivot(raw, twoDigitYearPivot)
          if (corrected) {
            // Rewrite input text so calendar header also reflects corrected year immediately
            const inputEl = event.target as HTMLInputElement
            inputEl.value = formatDdMmYyyy(corrected)
            onChange?.(corrected, undefined)
          }
        }
      }
      onChangeRaw?.(event)
    },
    [
      enableTwoDigitYearInput,
      twoDigitYearPivot,
      showTimeSelectOnly,
      parseWithPivot,
      formatDdMmYyyy,
      onChange,
      onChangeRaw,
    ],
  )

  return (
    <StyledDatePickerWrapper>
      <StyledDatePicker
        dateFormat={effectiveDateFormat}
        showPopperArrow={false}
        onChange={handleChange}
        onChangeRaw={handleChangeRaw}
        {...(inputProps ? {inputProps} : {})}
        {...rest}
      />
      <Icon
        size="sm"
        name={showTimeSelectOnly ? 'Clock' : 'Calendar'}
        color={theme.colors.grey.main}
      />
    </StyledDatePickerWrapper>
  )
}
